/**
 * KPI targets — build 6B.
 *
 * Every measure with the target it is graded against and where that target
 * came from: agreed, proposed (a benchmark, not yet agreed), the pack's, or
 * none. Someone holding `kpi:target` can propose or change a target, agree
 * one, or retire the site's own (the measure then falls back to the pack's
 * target, or is shown ungraded). Who did it and when is shown on the row and
 * kept in the history.
 */
import { Fragment, useState } from "react";
import { hasPermission, PERMISSION } from "../../api/dataSource";
import { useKpiTargetHistory, useRetireKpiTarget, useSetKpiTarget } from "../../api/entryFormQueries";
import { useKpis } from "../../api/queries";
import { useSession } from "../../app/SessionContext";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Segmented,
  Skeleton,
  Table,
} from "../../design/components";
import { kpiTargetRefusal, refusalText, retireRefusal, type TargetStatus } from "../../domain/entryForms";
import { formatKpiTarget, type Kpi } from "../../domain/kpis";
import { locale, t } from "../../i18n";
import s from "./EntryForms.module.css";

export function TargetBadge({ kpi }: { kpi: Pick<Kpi, "target" | "targetStatus"> }) {
  if (kpi.target == null) return <span className={`${s.badge} ${s.pack}`}>{t("NO TARGET")}</span>;
  if (kpi.targetStatus === "agreed") return <span className={`${s.badge} ${s.agreed}`}>{t("AGREED")}</span>;
  if (kpi.targetStatus === "proposed") return <span className={`${s.badge} ${s.proposed}`}>{t("PROPOSED")}</span>;
  return <span className={`${s.badge} ${s.pack}`}>{t("FROM THE PACK")}</span>;
}

export function when(at: string | null | undefined): string {
  if (!at) return "";
  const d = new Date(at);
  return Number.isNaN(d.getTime())
    ? at
    : d.toLocaleDateString(locale() ?? "en-US", { day: "numeric", month: "short", year: "numeric" });
}

type Mode = { key: string; kind: "set" | "retire" | "history" } | null;

export function KpiTargetsCard({ collapsible = true }: { collapsible?: boolean }) {
  const session = useSession();
  const kpis = useKpis();
  const canSet = hasPermission(session.principal, PERMISSION.kpiTarget);
  const [mode, setMode] = useState<Mode>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const list = (kpis.data ?? []).filter((k) => k.unit !== "" || k.value != null || k.target != null);
  const proposed = list.filter((k) => k.targetStatus === "proposed").length;
  const agreed = list.filter((k) => k.targetStatus === "agreed").length;

  return (
    <Card
      title="KPI targets"
      sub="Proposed until someone agrees them."
      collapsible={collapsible}
      collapseKey="kpi-targets"
      testId="kpi-targets"
    >
      {kpis.isPending ? (
        <Skeleton rows={4} />
      ) : kpis.isError ? (
        <ErrorState error={kpis.error} retry={() => void kpis.refetch()} />
      ) : list.length === 0 ? (
        <EmptyState>{t("No measures are installed for this site.")}</EmptyState>
      ) : (
        <>
          <p className={s.muted}>
            {t("{agreed} agreed, {proposed} proposed, {rest} from the pack or none.", {
              agreed,
              proposed,
              rest: list.length - agreed - proposed,
            })}{" "}
            {canSet
              ? t("Set a target, agree it, or retire it.")
              : t("Only an accountable manager or an administrator can change targets.")}
          </p>
          {saved && (
            <p className={s.ok} role="status">
              {saved}
            </p>
          )}
          <div className={s.scroll}>
            <Table>
              <thead>
                <tr>
                  <th>{t("Measure")}</th>
                  <th className={s.num}>{t("Target")}</th>
                  <th>{t("Status")}</th>
                  <th>{t("Basis")}</th>
                  <th>{t("Set by")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.map((k) => (
                  <Fragment key={k.key}>
                    <tr data-testid={`kpi-target-${k.key}`}>
                      <td>{t(k.label)}</td>
                      <td className={s.num}>
                        {k.target == null
                          ? "—"
                          : `${k.direction === "up" ? "≥" : "≤"} ${formatKpiTarget(k)} ${k.unit}`.trim()}
                      </td>
                      <td>
                        <TargetBadge kpi={k} />
                      </td>
                      <td className={s.muted}>
                        {k.targetBasis ?? (k.targetStatus === "pack" ? t("the pack's declaration") : "")}
                      </td>
                      <td className={s.muted}>
                        {/* a target set by a script has a date and no person */}
                        {k.targetSetBy
                          ? `${k.targetSetBy}${k.targetSetAt ? `, ${when(k.targetSetAt)}` : ""}`
                          : when(k.targetSetAt)}
                      </td>
                      <td>
                        <div className={s.actions}>
                          {canSet && (
                            <Button size="sm" onClick={() => setMode({ key: k.key, kind: "set" })}>
                              {k.targetStatus === "proposed"
                                ? t("Agree or change")
                                : k.target == null
                                  ? t("Set")
                                  : t("Change")}
                            </Button>
                          )}
                          {canSet && k.targetScope === "site" && (
                            <Button size="sm" variant="ghost" onClick={() => setMode({ key: k.key, kind: "retire" })}>
                              {t("Retire")}
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => setMode({ key: k.key, kind: "history" })}>
                            {t("History")}
                          </Button>
                        </div>
                      </td>
                    </tr>
                    {mode?.key === k.key && (
                      <tr className={s.formRow}>
                        <td colSpan={6}>
                          {mode.kind === "set" && (
                            <TargetForm
                              kpi={k}
                              known={list.map((x) => x.key)}
                              onDone={(msg) => {
                                setSaved(msg);
                                setMode(null);
                              }}
                              onCancel={() => setMode(null)}
                            />
                          )}
                          {mode.kind === "retire" && (
                            <RetireForm
                              kpi={k}
                              onDone={(msg) => {
                                setSaved(msg);
                                setMode(null);
                              }}
                              onCancel={() => setMode(null)}
                            />
                          )}
                          {mode.kind === "history" && <History kpi={k} onClose={() => setMode(null)} />}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </Table>
          </div>
        </>
      )}
    </Card>
  );
}

function TargetForm({
  kpi,
  known,
  onDone,
  onCancel,
}: {
  kpi: Kpi;
  known: string[];
  onDone: (msg: string) => void;
  onCancel: () => void;
}) {
  const set = useSetKpiTarget();
  const [value, setValue] = useState(kpi.target == null ? "" : String(kpi.target));
  const [status, setStatus] = useState<TargetStatus>(kpi.targetStatus === "proposed" ? "agreed" : "proposed");
  const [basis, setBasis] = useState(kpi.targetStatus === "pack" ? "" : (kpi.targetBasis ?? ""));
  const [why, setWhy] = useState<string | null>(null);

  const save = async () => {
    const input = { metricKey: kpi.key, target: value.trim() === "" ? Number.NaN : Number(value), status, basis };
    const refused = kpiTargetRefusal(input, known);
    if (refused) return setWhy(refused);
    setWhy(null);
    try {
      await set.mutateAsync(input);
      onDone(
        t("{kpi}: target {value} saved as {status}.", {
          kpi: t(kpi.label),
          value: input.target,
          status: status === "agreed" ? t("agreed") : t("proposed"),
        }),
      );
    } catch (e) {
      setWhy(refusalText(e));
    }
  };

  return (
    <div className={s.form} data-testid="kpi-target-form">
      <Field label="Target">
        <Input
          type="number"
          step="any"
          inputMode="decimal"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          aria-label={t("Target for {kpi}", { kpi: t(kpi.label) })}
        />
      </Field>
      <div>
        <Segmented
          ariaLabel="Target status"
          value={status}
          onChange={setStatus}
          options={[
            { value: "proposed", label: "Proposed" },
            { value: "agreed", label: "Agreed" },
          ]}
        />
      </div>
      <div className={s.wide}>
        <Field label="Basis — what the figure comes from">
          <Input
            value={basis}
            onChange={(e) => setBasis(e.target.value)}
            placeholder={t("e.g. service agreement, clause 4.2")}
            maxLength={500}
          />
        </Field>
      </div>
      {kpi.packTarget != null && (
        <p className={`${s.muted} ${s.wide}`}>{t("The pack's target is {value}.", { value: kpi.packTarget })}</p>
      )}
      {why && (
        <p className={`${s.err} ${s.wide}`} role="alert">
          {why}
        </p>
      )}
      <div className={`${s.actions} ${s.wide}`}>
        <Button variant="primary" onClick={() => void save()} disabled={set.isPending}>
          {set.isPending ? t("Saving…") : t("Save target")}
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          {t("Cancel")}
        </Button>
      </div>
    </div>
  );
}

function RetireForm({ kpi, onDone, onCancel }: { kpi: Kpi; onDone: (msg: string) => void; onCancel: () => void }) {
  const retire = useRetireKpiTarget();
  const [reason, setReason] = useState("");
  const [why, setWhy] = useState<string | null>(null);
  const go = async () => {
    const refused = retireRefusal(reason);
    if (refused) return setWhy(refused);
    setWhy(null);
    try {
      await retire.mutateAsync({ metricKey: kpi.key, reason: reason.trim() });
      onDone(
        kpi.packTarget != null
          ? t("{kpi}: target retired. The pack's target applies again.", { kpi: t(kpi.label) })
          : t("{kpi}: target retired. The measure is now shown ungraded.", { kpi: t(kpi.label) }),
      );
    } catch (e) {
      setWhy(refusalText(e));
    }
  };
  return (
    <div className={s.form} data-testid="kpi-retire-form">
      <div className={s.wide}>
        <Field label="Why retire it?">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} />
        </Field>
      </div>
      {why && (
        <p className={`${s.err} ${s.wide}`} role="alert">
          {why}
        </p>
      )}
      <div className={`${s.actions} ${s.wide}`}>
        <Button variant="danger" onClick={() => void go()} disabled={retire.isPending}>
          {retire.isPending ? t("Retiring…") : t("Retire target")}
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          {t("Cancel")}
        </Button>
      </div>
    </div>
  );
}

function History({ kpi, onClose }: { kpi: Kpi; onClose: () => void }) {
  const h = useKpiTargetHistory(kpi.key);
  return (
    <div data-testid="kpi-target-history">
      {h.isPending ? (
        <Skeleton rows={2} />
      ) : h.isError ? (
        <ErrorState error={h.error} />
      ) : (h.data ?? []).length === 0 ? (
        <p className={s.muted}>{t("No changes recorded for this target yet.")}</p>
      ) : (
        <ul className={s.history}>
          {(h.data ?? []).map((c, i) => (
            <li key={i}>
              {c.action === "retired"
                ? t("{when} — retired by {who}: {reason}", {
                    when: when(c.at),
                    who: c.by ?? "—",
                    reason: c.reason ?? "",
                  })
                : t("{when} — {who} set {value} ({status}): {basis}", {
                    when: when(c.at),
                    who: c.by ?? "—",
                    value: c.target ?? "—",
                    status: c.status === "agreed" ? t("agreed") : t("proposed"),
                    basis: c.basis ?? "",
                  })}
            </li>
          ))}
        </ul>
      )}
      <Button size="sm" variant="ghost" onClick={onClose}>
        {t("Close")}
      </Button>
    </div>
  );
}
